import { recordAnalyticsDiagnostic } from './analyticsDiagnostics';
import type { CapturedEvent, CapturedEventInput } from '@nextmar/snowplow-sdk';
export type { CapturedEvent, CapturedEventInput } from '@nextmar/snowplow-sdk';
import { createZeeloolSnowplowDataAdapters } from './integrations/snowplow-sdk/adapters';
import type { TrackingEvent } from './types';

/** 有限队列的接收结果仅表示门面所有权，不代表网络或入库成功。 */
export interface CaptureResult {
  accepted: boolean;
  reason?: string;
}
/** 捕获页上下文复用SDK公共合同，避免本地类型漂移。 */
export type CapturedSnapshot = Omit<CapturedEventInput, 'logicalEventId' | 'sequence' | 'event'>;
/** 身份动作与事件共享顺序，不保存读取当前业务状态的闭包。 */
export type AnalyticsIdentityCommand =
  | { kind: 'login'; uuid: string; userId?: string }
  | { kind: 'logout' };
/** 单端交接协议；ready false 不消耗事件。 */
export interface EarlyEventProvider {
  enabled: boolean;
  ready: () => boolean;
  dispatch: (
    input: CapturedEventInput,
    trackingEvent?: TrackingEvent,
  ) => CaptureResult | Promise<CaptureResult>;
  identity?: () => { anonymousId?: string; distinctId?: string };
  applyIdentity?: (command: AnalyticsIdentityCommand) => boolean | Promise<boolean>;
}
/** 显式捕获参数；历史 PageLeave 可复用之前的页面快照。 */
export interface CaptureOptions {
  providers?: string[];
  eventId?: string;
  snapshot?: CapturedSnapshot;
  trackingEvent?: TrackingEvent;
}
/** 每端独立所有权。 */
type Outcome =
  | 'pending'
  | 'dispatching'
  | 'handed_off'
  | 'unknown'
  | 'expired'
  | 'disabled'
  | 'identity_unresolvable'
  | 'unsupported_runtime'
  | 'rejected';
/** 内存中只保存数据与结果，不保存 DOM 或事件业务闭包。 */
interface Entry {
  input: CapturedEventInput;
  trackingEvent?: TrackingEvent;
  outcomes: Record<string, Outcome>;
  bytes: number;
}
/** 身份命令保存前后阶段，完成后再放行下一阶段。 */
interface IdentityEntry {
  occurredAt: number;
  sequence: number;
  fromEpoch: number;
  epoch: number;
  command: AnalyticsIdentityCommand;
  done: boolean;
  dispatching?: boolean;
}
/** 队列参数支持纯时钟测试与部署调优。 */
interface QueueOptions {
  now?: () => number;
  maxEvents?: number;
  ttlMs?: number;
  maxBytes?: number;
  maxEventBytes?: number;
  snapshot?: () => CapturedSnapshot;
}

/** 安全深拷贝并冻结 JSON 数据；0/false 保留，DOM/函数/循环引用拒绝。 */
function immutable<T>(value: T): T {
  const json = JSON.stringify(value, (_key, item) => {
    if (
      typeof item === 'function' ||
      typeof item === 'symbol' ||
      (typeof Node !== 'undefined' && item instanceof Node)
    )
      throw new Error('invalid_snapshot');
    return item;
  });
  const clone = JSON.parse(json) as T;
  /** 递归冻结防止 provider 修改另一端快照。 */
  const freeze = (item: unknown): void => {
    if (item && typeof item === 'object') {
      Object.values(item).forEach(freeze);
      Object.freeze(item);
    }
  };
  freeze(clone);
  return clone;
}

/** 所有浏览器事件的一层有限所有者，每次 drain 最多派发20条后让出主线程。 */
export class EarlyEventQueue {
  /** 文档内单调序号与身份阶段。 */
  private sequence = 0;
  private epoch = 0;
  private entries: Entry[] = [];
  private commands: IdentityEntry[] = [];
  private providers = new Map<string, EarlyEventProvider>();
  private identities = new Map<number, { anonymousId: string; distinctId?: string }>();
  private blockedEpochs = new Set<number>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private counts: Record<string, number> = {
    expired: 0,
    overflow: 0,
    unknown: 0,
    disabled: 0,
    identity_unresolvable: 0,
    handed_off: 0,
  };
  /** 配置只在创建时固定，SSR调用者不会共享实例。 */
  constructor(private readonly options: QueueOptions = {}) {}
  /** 当前 epoch 供捕获快照使用。 */
  getIdentityEpoch(): number {
    return this.epoch;
  }
  /** 仅暴露已确认阶段的真实身份，不读取缓存。 */
  getCurrentIdentity(): { anonymousId: string; distinctId?: string } | undefined {
    return this.identities.get(this.epoch);
  }
  /** 挂载独立 provider，配置禁用事件立即终结，不在日后追溯。 */
  register(name: string, provider: EarlyEventProvider): void {
    this.providers.set(name, provider);
  }
  /** 同步接收 immutable 事件，不等待脚本或 provider。 */
  capture(event: CapturedEvent, options: CaptureOptions = {}): CaptureResult {
    this.expire();
    const targets = options.providers ?? ['sensors', 'snowplow'];
    const active = targets.filter((name) => this.providers.get(name)?.enabled !== false);
    if (!active.length) {
      this.counts.disabled += targets.length;
      return { accepted: false, reason: 'disabled' };
    }
    try {
      const snapshot = options.snapshot ?? this.options.snapshot?.();
      if (!snapshot) return { accepted: false, reason: 'invalid_snapshot' };
      const sequence = ++this.sequence;
      const input = immutable({
        ...snapshot,
        identityEpoch: options.snapshot?.identityEpoch ?? this.epoch,
        logicalEventId: `${options.eventId ?? snapshot.documentId}:${sequence}`,
        sequence,
        event,
        // 专用注册事件沿用SDK根uid=uuid语义，冻结时确定而非回放时读取用户。
        ...(event.kind === 'signUp' ? { userId: String(event.user.uuid) } : {}),
      });
      const trackingEvent = options.trackingEvent ? immutable(options.trackingEvent) : undefined;
      const bytes = new TextEncoder().encode(JSON.stringify({ input, trackingEvent })).length;
      // 容量压力优先回收同页面重复曝光，仍不足则明确拒绝，不静默丢关键事件。
      if (
        this.entries.length >= (this.options.maxEvents ?? 1000) ||
        this.entries.reduce((sum, item) => sum + item.bytes, 0) + bytes >
          (this.options.maxBytes ?? 1048576)
      )
        this.reclaimDuplicateExposures();
      if (
        bytes > (this.options.maxEventBytes ?? 65536) ||
        this.entries.length >= (this.options.maxEvents ?? 1000) ||
        this.entries.reduce((sum, item) => sum + item.bytes, 0) + bytes >
          (this.options.maxBytes ?? 1048576)
      ) {
        this.counts.overflow++;
        recordAnalyticsDiagnostic('overflow', 'queue');
        return { accepted: false, reason: 'overflow' };
      }
      this.entries.push({
        input,
        trackingEvent,
        bytes,
        outcomes: Object.fromEntries(active.map((name) => [name, 'pending'])),
      });
      this.schedule();
      // 退出事件同一调用内尽力交接已ready传输，不等待下一次timer。
      if (event.kind === 'pageLeave') void this.flush();
      else void this.drain();
      return { accepted: true };
    } catch {
      this.counts.invalid_snapshot = (this.counts.invalid_snapshot ?? 0) + 1;
      recordAnalyticsDiagnostic('invalid_snapshot', 'queue');
      return { accepted: false, reason: 'invalid_snapshot' };
    }
  }
  /** 只回收尚未交接且内容完全相同的低优先级曝光。 */
  private reclaimDuplicateExposures(): void {
    const seen = new Set<string>();
    this.entries = this.entries.filter((entry) => {
      if (
        entry.input.event.kind !== 'webExp' ||
        Object.values(entry.outcomes).some((state) => state !== 'pending')
      )
        return true;
      const key = JSON.stringify([
        entry.input.pageViewId,
        entry.input.identityEpoch,
        entry.input.event,
        Object.keys(entry.outcomes),
      ]);
      if (!seen.has(key)) {
        seen.add(key);
        return true;
      }
      this.counts.superseded = (this.counts.superseded ?? 0) + Object.keys(entry.outcomes).length;
      recordAnalyticsDiagnostic('superseded', 'queue', Object.keys(entry.outcomes).length);
      return false;
    });
  }

  /** 身份操作也占用有界容量，拒绝时不推进阶段。 */
  identity(command: AnalyticsIdentityCommand): CaptureResult {
    if (this.providers.get('sensors')?.enabled === false) {
      this.counts.disabled++;
      return { accepted: false, reason: 'disabled' };
    }
    let frozenCommand: AnalyticsIdentityCommand;
    try {
      frozenCommand = immutable(command);
      if (
        frozenCommand.kind === 'login' &&
        (typeof frozenCommand.uuid !== 'string' ||
          !frozenCommand.uuid.trim() ||
          ['null', 'undefined'].includes(frozenCommand.uuid.trim().toLowerCase()))
      )
        return { accepted: false, reason: 'invalid_identity' };
    } catch {
      return { accepted: false, reason: 'invalid_identity' };
    }
    if (this.commands.length >= (this.options.maxEvents ?? 1000)) {
      this.counts.overflow++;
      recordAnalyticsDiagnostic('overflow', 'queue');
      return { accepted: false, reason: 'overflow' };
    }
    this.commands.push({
      occurredAt: this.now(),
      sequence: ++this.sequence,
      fromEpoch: this.epoch,
      epoch: ++this.epoch,
      command: frozenCommand,
      done: false,
    });
    this.schedule();
    return { accepted: true };
  }
  /** 返回脱敏计数，无业务参数/完整身份。 */
  getDiagnostics(): Record<string, number> {
    return {
      ...this.counts,
      dispatching: this.entries.reduce(
        (n, entry) =>
          n + Object.values(entry.outcomes).filter((state) => state === 'dispatching').length,
        0,
      ),
      pending: this.entries.reduce(
        (n, item) => n + Object.values(item.outcomes).filter((v) => v === 'pending').length,
        0,
      ),
    };
  }
  /** 每端dispatching标记负责并发互斥，不能用全局Promise锁阻止其它端与TTL。 */
  drain(): Promise<void> {
    return this.flush();
  }
  /** 只使用可注入的发生时钟计算TTL。 */
  private now(): number {
    return (this.options.now ?? Date.now)();
  }
  /** 有界轮询仅在记录仍保留时运行，到期清空，不使用无界interval。 */
  private schedule(): void {
    if (this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.drain().finally(() => {
        if (this.entries.length || this.commands.length) this.schedule();
      });
    }, 100);
    this.timer.unref?.();
  }
  /** 记录每端终态并维护诊断。 */
  private finish(entry: Entry, name: string, result: Outcome): void {
    entry.outcomes[name] = result;
    this.counts[result] = (this.counts[result] ?? 0) + 1;
    if (
      result !== 'handed_off' &&
      result !== 'pending' &&
      result !== 'dispatching' &&
      result !== 'disabled'
    )
      recordAnalyticsDiagnostic(result, name);
  }
  /** 过期不会静默shift；按provider记录损失原因。 */
  private expire(): void {
    for (const entry of this.entries)
      if (this.now() - entry.input.occurredAt >= (this.options.ttlMs ?? 60000))
        for (const [name, state] of Object.entries(entry.outcomes))
          if (state === 'pending') this.finish(entry, name, 'expired');
          else if (state === 'dispatching') this.finish(entry, name, 'unknown');
    for (const command of this.commands)
      if (this.now() - command.occurredAt >= (this.options.ttlMs ?? 60000)) {
        command.done = true;
        this.blockedEpochs.add(command.epoch);
      }
    this.commands = this.commands.filter((command) => !command.done);
    this.entries = this.entries.filter((entry) =>
      Object.values(entry.outcomes).some((state) => state === 'pending' || state === 'dispatching'),
    );
    // 阶段历史只保留仍被有界记录引用的身份，避免长期SPA累积无限身份Map。
    const epochs = new Set([
      0,
      this.epoch,
      ...this.entries.map((entry) => entry.input.identityEpoch),
      ...this.commands.flatMap((command) => [command.fromEpoch, command.epoch]),
    ]);
    for (const epoch of Array.from(this.identities.keys()))
      if (!epochs.has(epoch)) this.identities.delete(epoch);
    for (const epoch of Array.from(this.blockedEpochs))
      if (!epochs.has(epoch)) this.blockedEpochs.delete(epoch);
  }
  /** 初始真实匿名身份仅绑定到当前可恢复阶段。 */
  private readIdentity(epoch: number): void {
    if (this.identities.has(epoch)) return;
    const identity = this.providers.get('sensors')?.identity?.();
    const value = identity?.anonymousId;
    if (typeof value === 'string' && value.trim() && !['null', 'undefined'].includes(value.trim()))
      this.identities.set(epoch, { ...identity, anonymousId: value.trim() });
  }
  /** 派发按序处理各端，某端不可用不会阻塞另一端。 */
  private async flush(): Promise<void> {
    this.expire();
    this.readIdentity(0);
    let budget = 0;
    const ordered = [...this.entries, ...this.commands].sort(
      (a, b) =>
        ('input' in a ? a.input.sequence : a.sequence) -
        ('input' in b ? b.input.sequence : b.sequence),
    );
    for (const item of ordered) {
      if (!('input' in item)) {
        if (item.done || item.dispatching) continue;
        const sensors = this.providers.get('sensors');
        const preceding = this.entries.some(
          (entry) =>
            entry.input.sequence < item.sequence &&
            ['pending', 'dispatching'].includes(entry.outcomes.sensors),
        );
        if (!sensors?.ready() || preceding) continue;
        this.readIdentity(item.fromEpoch);
        item.dispatching = true;
        /** 身份结果晚到只结束原命令，不能放行已经过期的阶段。 */
        const settleIdentity = (success: boolean): void => {
          if (item.done) return;
          if (!success) this.blockedEpochs.add(item.epoch);
          else this.readIdentity(item.epoch);
          item.done = true;
          item.dispatching = false;
        };
        try {
          const result = sensors.applyIdentity?.(item.command) ?? false;
          if (typeof result === 'boolean') settleIdentity(result);
          else {
            void result.then(settleIdentity, () => settleIdentity(false));
            // 只让立即完成的Promise运行，不等待远端/永久pending命令。
            await Promise.resolve();
          }
        } catch {
          settleIdentity(false);
        }
        continue;
      }
      const entry = item;
      const unresolvedCommand = this.commands.some(
        (command) => !command.done && command.epoch <= entry.input.identityEpoch,
      );
      for (const name of Object.keys(entry.outcomes)) {
        if (entry.outcomes[name] !== 'pending') continue;
        const provider = this.providers.get(name);
        if (provider?.enabled === false) {
          this.finish(entry, name, 'disabled');
          continue;
        }
        if (!provider?.ready()) continue;
        // 同端前一交接未确认时保持顺序，另一端仍可继续。
        if (
          this.entries.some(
            (previous) =>
              previous.input.sequence < entry.input.sequence &&
              previous.outcomes[name] === 'dispatching',
          )
        )
          continue;
        const analytics = name === 'sensors' || name === 'snowplow';
        if (analytics && unresolvedCommand) continue;
        if (analytics && this.blockedEpochs.has(entry.input.identityEpoch)) {
          this.finish(entry, name, 'identity_unresolvable');
          continue;
        }
        const identity = this.identities.get(entry.input.identityEpoch);
        // 没有身份能力的纯测试/广告适配器不触发神策屏障；真实ZV providers均提供identity getter。
        if (analytics && this.providers.get('sensors')?.identity && !identity) continue;
        const input = identity
          ? {
              ...entry.input,
              identity,
              identityBinding: entry.input.identity.anonymousId
                ? ('captured' as const)
                : ('deferred' as const),
            }
          : entry.input;
        entry.outcomes[name] = 'dispatching';
        /** 结果未知/校验失败为终态，只有明确未交接的初始化拒绝可重试。 */
        const settle = (result: CaptureResult): void => {
          if (entry.outcomes[name] !== 'dispatching') return;
          if (result.accepted || result.reason === 'duplicate')
            this.finish(entry, name, 'handed_off');
          else if (result.reason === 'unsupported_runtime')
            this.finish(entry, name, 'unsupported_runtime');
          else if (result.reason === 'identity_unresolvable')
            this.finish(entry, name, 'identity_unresolvable');
          else if (result.reason === 'dispatch_unknown' || result.reason === 'unknown')
            this.finish(entry, name, 'unknown');
          else if (['not_ready', 'initializing', 'idle'].includes(result.reason ?? ''))
            entry.outcomes[name] = 'pending';
          else this.finish(entry, name, 'rejected');
        };
        try {
          const result = provider.dispatch(input, entry.trackingEvent);
          if ('then' in result) {
            void result.then(settle, () => settle({ accepted: false, reason: 'dispatch_unknown' }));
            await Promise.resolve();
          } else settle(result);
        } catch {
          settle({ accepted: false, reason: 'dispatch_unknown' });
        }
        if (++budget >= 20) {
          budget = 0;
          await new Promise<void>((resolve) => setTimeout(resolve, 0));
        }
      }
    }
    this.commands = this.commands.filter((item) => !item.done);
    this.expire();
  }
}

/** pageViewId遵循web_page schema的UUID，fallback不承担匿名身份职责。 */
function createPageUuid(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const value = Math.floor(Math.random() * 16);
    return (char === 'x' ? value : (value & 3) | 8).toString(16);
  });
}

/** 浏览器document持有门面，服务器从不保存用户队列。 */
interface AnalyticsWindow extends Window {
  __zeeloolEarlyEvents?: EarlyEventQueue;
  __zeeloolAnalyticsDocumentId?: string;
  __zeeloolAnalyticsPage?: { key: string; url: string; id: string; referrer: string };
}
/** 同步冻结真实页面、用户和归因，身份缓存不作为ready证据。 */
export function captureSnapshot(): CapturedSnapshot {
  const win = window as AnalyticsWindow;
  win.__zeeloolAnalyticsDocumentId ??=
    globalThis.crypto?.randomUUID?.() ?? `doc-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const query = new URLSearchParams(location.search);
  ['showLogin', 'from', 'reason'].forEach((key) => query.delete(key));
  const routeKey = `${location.pathname}?${query.toString()}`;
  if (win.__zeeloolAnalyticsPage?.key !== routeKey)
    win.__zeeloolAnalyticsPage = {
      key: routeKey,
      url: location.href,
      id: createPageUuid(),
      referrer: win.__zeeloolAnalyticsPage?.url ?? document.referrer,
    };
  const adapters = createZeeloolSnowplowDataAdapters();
  const user = adapters.getUser?.() ?? {};
  const identity = win.__zeeloolEarlyEvents?.getCurrentIdentity();
  return immutable({
    documentId: win.__zeeloolAnalyticsDocumentId,
    pageViewId: win.__zeeloolAnalyticsPage.id,
    occurredAt: Date.now(),
    monotonicTime: performance.now(),
    identityEpoch: win.__zeeloolEarlyEvents?.getIdentityEpoch() ?? 0,
    page: {
      url: location.href,
      title: document.title,
      referrer: win.__zeeloolAnalyticsPage.referrer,
      viewport: `${innerWidth}x${innerHeight}`,
    },
    identity: identity ?? { anonymousId: '' },
    user,
    userId: user.isLoggedIn ? (user.customerId ?? null) : null,
    attribution: adapters.getAttribution?.() ?? {},
    platformLanguage: adapters.getPlatformLanguage?.(),
    identityBinding: identity ? 'captured' : 'deferred',
  });
}
/** 浏览器惰性单例；SSR不创建或读取用户数据。 */
export function getEarlyEventQueue(): EarlyEventQueue | null {
  if (typeof window === 'undefined') return null;
  const win = window as AnalyticsWindow;
  return (win.__zeeloolEarlyEvents ??= new EarlyEventQueue({ snapshot: captureSnapshot }));
}
/** 专用业务入口从最早调用开始接收。 */
export function captureAnalyticsEvent(
  event: CapturedEvent,
  options?: CaptureOptions,
): CaptureResult {
  return getEarlyEventQueue()?.capture(event, options) ?? { accepted: false, reason: 'server' };
}
/** 普通事件复用相同快照，eventId在capture时保留。 */
export function captureTrackingEvent(
  event: TrackingEvent,
  providers?: string[],
  snapshot?: CapturedSnapshot,
): CaptureResult {
  return captureAnalyticsEvent(
    {
      kind: 'track',
      eventName: event.eventName ?? '',
      properties: event.properties,
      options: { sensorsEventId: event.eventId, ...event.options },
    },
    { providers, eventId: event.eventId, snapshot, trackingEvent: event },
  );
}
/** 业务身份不等待网络；分析身份动作按原顺序进入门面。 */
export function advanceAnalyticsIdentity(command: AnalyticsIdentityCommand): CaptureResult {
  return getEarlyEventQueue()?.identity(command) ?? { accepted: false, reason: 'server' };
}
